/**
 * BZO ingestion — the "we only cover Meilen and Egg" objection, removed.
 *
 * Two things shape this screen. First, the primary case is on-demand ingestion
 * of a PUBLIC BZO document: one Gemeinde, one file, and one attestation that
 * the file really is the Gemeinde's ordinance and not somebody's dossier. (A
 * prospect can use the same screen; their result lands as a draft either way,
 * which is what makes that safe.) Second, the result is a DRAFT pack — the lead
 * says so before anyone uploads anything, so nobody arrives at the verification
 * queue expecting a finished rulebase.
 *
 * The file check is a courtesy that mirrors the dossier intake: the content is
 * sniffed for `%PDF-`, not the extension, and the server refuses again anyway.
 */
import { useState } from 'react'
import { FileText, Upload, X } from 'lucide-react'

import { api, ApiError } from '@/api/client'
import { ErrorNote } from '@/components/ErrorNote'
import { Icon } from '@/components/Icon'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { MAX_FILE_BYTES, formatBytes, looksLikePdf } from '@/features/upload/uploadRules'
import { useI18n } from '@/i18n'
import { buildPath, navigate } from '@/lib/router'
import type { WorkspaceRef } from '@/api/types'

type FileProblem = 'notPdf' | 'tooLarge' | null

export function BzoIngestScreen({ workspace }: { workspace: WorkspaceRef }) {
  const { t } = useI18n()
  const [file, setFile] = useState<File | null>(null)
  const [problem, setProblem] = useState<FileProblem>(null)
  const [gemeinde, setGemeinde] = useState('')
  const [kanton, setKanton] = useState('ZH')
  const [attested, setAttested] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** Set only after the server answered 409. Never a default. */
  const [replaceOffered, setReplaceOffered] = useState(false)
  const [replace, setReplace] = useState(false)

  const choose = (files: FileList | null) => {
    const picked = files?.[0] ?? null
    if (!picked) return
    setError(null)
    if (picked.size > MAX_FILE_BYTES) {
      setFile(picked)
      setProblem('tooLarge')
      return
    }
    void looksLikePdf(picked).then((ok) => {
      setFile(picked)
      setProblem(ok ? null : 'notPdf')
    })
  }

  const ready = file !== null && problem === null && gemeinde.trim() !== '' && attested && !busy

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!ready || !file) return
    setBusy(true)
    setError(null)
    try {
      const ingestion = await api.ingestBzo(workspace.id, {
        file,
        gemeinde: gemeinde.trim(),
        kanton: kanton.trim() || 'ZH',
        attested,
        replace,
      })
      navigate(
        buildPath('rulebaseIngestion', {
          workspaceId: workspace.id,
          ingestionId: ingestion.id,
        }),
      )
    } catch (cause) {
      // 409: a draft for this Gemeinde already exists. Replacing it is a
      // decision, so it becomes a checkbox the reader ticks — never a retry
      // this screen performs on its own.
      if (cause instanceof ApiError && cause.status === 409) setReplaceOffered(true)
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="flex max-w-[900px] flex-col gap-8" onSubmit={(e) => void submit(e)}>
      <section className="flex flex-col gap-3">
        <h1 className="font-display text-[32px] leading-tight font-semibold">
          {t('rulebase.ingestTitle')}
        </h1>
        <p className="max-w-[70ch] text-[15px] text-muted">{t('rulebase.ingestLead')}</p>
        <p className="text-[12px] text-muted-2">{t('rulebase.ingestScopeNote')}</p>
      </section>

      <Card>
        <CardHeader>
          <CardTitle>{t('rulebase.ingestGemeinde')}</CardTitle>
          <CardDescription>{t('rulebase.ingestScopeNote')}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="ingest-gemeinde" className="text-[13px] font-medium text-ink-2">
              {t('rulebase.ingestGemeinde')}
            </label>
            <input
              id="ingest-gemeinde"
              type="text"
              required
              value={gemeinde}
              onChange={(event) => setGemeinde(event.target.value)}
              placeholder={t('rulebase.ingestGemeindePlaceholder')}
              className="h-10 rounded-sm border border-border-strong bg-white px-3 text-[15px] text-ink placeholder:text-muted-2"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="ingest-kanton" className="text-[13px] font-medium text-ink-2">
              {t('rulebase.ingestKanton')}
            </label>
            <input
              id="ingest-kanton"
              type="text"
              value={kanton}
              onChange={(event) => setKanton(event.target.value)}
              className="num h-10 rounded-sm border border-border-strong bg-white px-3 text-[15px] text-ink"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('rulebase.ingestFileTitle')}</CardTitle>
          <CardDescription>
            {t('rulebase.ingestFileDescription', { max: formatBytes(MAX_FILE_BYTES) })}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div
            data-testid="bzo-dropzone"
            data-dragging={dragging}
            onDragOver={(event) => {
              event.preventDefault()
              setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault()
              setDragging(false)
              choose(event.dataTransfer.files)
            }}
            className={[
              'flex flex-col gap-3 rounded-md border border-dashed px-6 py-8 transition-colors duration-fast ease-enter',
              dragging ? 'border-accent bg-accent-soft' : 'border-border-strong bg-surface',
            ].join(' ')}
          >
            <p className="flex items-center gap-2 text-[14px] font-medium text-ink">
              <Icon icon={Upload} />
              {t('rulebase.ingestDropHere')}
            </p>
            <label htmlFor="bzo-file" className="text-[13px] text-muted">
              {t('rulebase.ingestOrChoose')}
            </label>
            <input
              id="bzo-file"
              type="file"
              accept="application/pdf,.pdf"
              onChange={(event) => {
                choose(event.target.files)
                event.target.value = ''
              }}
              className="block w-full text-[13px] text-ink file:me-3 file:rounded-sm file:border file:border-border-strong file:bg-white file:px-3 file:py-1.5 file:text-[13px] file:text-ink"
            />
          </div>

          <div
            className="flex flex-wrap items-center gap-3 text-[13px]"
            data-testid="bzo-file-state"
            data-problem={problem ?? (file ? 'accepted' : 'none')}
          >
            {file ? (
              <>
                <Icon
                  icon={problem ? X : FileText}
                  className={problem ? 'text-signal-red-text' : 'text-accent'}
                />
                <span className="text-ink">{file.name}</span>
                <span className="num text-[12px] text-muted">{formatBytes(file.size)}</span>
                {problem === 'notPdf' ? (
                  <span className="text-[12px] text-signal-red-text">
                    {t('rulebase.ingestNotPdf')}
                  </span>
                ) : null}
                {problem === 'tooLarge' ? (
                  <span className="text-[12px] text-signal-red-text">
                    {t('rulebase.ingestTooLarge', { max: formatBytes(MAX_FILE_BYTES) })}
                  </span>
                ) : null}
              </>
            ) : (
              <span className="text-muted">{t('rulebase.ingestNoFile')}</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* The BZO attestation. The dossier intake has its own FADP attestation
          for personal data; this one answers a different question — that the
          document being ingested is the Gemeinde's public ordinance and not
          somebody's file. The server requires it, the box is never pre-ticked,
          and the reason the submit is blocked stands next to the button. */}
      <Card>
        <CardHeader>
          <CardTitle>{t('rulebase.ingestAttestTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <label className="flex items-start gap-3 rounded-sm border border-border bg-surface px-4 py-3">
            <input
              type="checkbox"
              checked={attested}
              onChange={(event) => setAttested(event.target.checked)}
              className="mt-1 accent-accent"
            />
            <span className="text-[14px] text-ink">{t('rulebase.ingestAttestCheckbox')}</span>
          </label>
        </CardContent>
      </Card>

      {error ? <ErrorNote message={error} /> : null}

      {replaceOffered ? (
        <div
          role="note"
          data-testid="ingest-replace"
          className="flex flex-col gap-2 rounded-sm border border-dashed border-border-strong bg-surface-2 px-4 py-3"
        >
          <p className="text-[13px] font-medium text-ink-2">{t('rulebase.ingestReplaceTitle')}</p>
          <label className="flex items-start gap-3 text-[13px] text-ink">
            <input
              type="checkbox"
              checked={replace}
              onChange={(event) => setReplace(event.target.checked)}
              className="mt-1 accent-accent"
            />
            <span>{t('rulebase.ingestReplace')}</span>
          </label>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={!ready}>
          <Icon icon={Upload} />
          {busy ? t('rulebase.ingestSubmitting') : t('rulebase.ingestSubmit')}
        </Button>
        {!ready && !busy ? (
          <span className="text-[12px] text-muted" data-testid="ingest-blocked">
            {file === null || problem !== null
              ? t('rulebase.ingestBlockedFile')
              : gemeinde.trim() === ''
                ? t('rulebase.ingestBlockedGemeinde')
                : t('rulebase.ingestBlockedAttest')}
          </span>
        ) : null}
      </div>
    </form>
  )
}
